"use client";

import { useEffect } from "react";
import { recordRender } from "@/lib/perf/perf-store";

/**
 * ReactScanInit
 *
 * Bootstraps React Scan's programmatic API and wires its `onRender` callback
 * to the perf-store so that every component render is automatically recorded.
 *
 * - Renders nothing to the DOM.
 * - Completely tree-shaken in production (guarded by NODE_ENV check at the
 *   call-site in layout.tsx — this file is never imported in prod builds).
 * - Must be rendered inside the React tree so that React Scan can observe
 *   the full component hierarchy.
 */
export function ReactScanInit() {
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const { scan } = await import("react-scan");

        if (cancelled) return;

        scan({
          animationSpeed: "fast",
          enabled: true,
          onRender(fiber, renders) {
            const name: string =
              (typeof fiber.type === "function" && (fiber.type.displayName || fiber.type.name)) ||
              (typeof fiber.type === "string" && fiber.type) ||
              "Anonymous";

            for (const render of renders) {
              // `render.time` is the render duration in ms provided by React Scan.
              // `render.unnecessary` is true when React Scan detected the output
              // was identical to the previous render (wasted render).
              recordRender(name, render.time ?? 0, render.unnecessary ?? false);
            }
          },
          // Show the built-in React Scan overlay so visual feedback is still
          // available alongside the programmatic data collection.
          showToolbar: true,
        });
      } catch (err) {
        // React Scan is a dev-only tool — a failure here must never break the app.
        console.warn("[ReactScanInit] Failed to initialize react-scan:", err);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
